/**
 * Hook réutilisable pour obtenir un step-up token (réauthentification 5 min)
 * avant une action sensible : téléchargement rapport, transmission spécialiste,
 * suppression dossier.
 *
 * Usage :
 *   const { ensureStepUp, StepUpDialog } = useStepUp();
 *   const token = await ensureStepUp({ purpose: "verifier.download" });
 *   if (!token) return; // utilisateur a annulé
 *   apiClient.get(url, { headers: { "X-Step-Up-Token": token } });
 *
 * Rend le `<StepUpDialog />` qui doit être inséré dans le JSX du composant
 * appelant pour afficher la modale de mot de passe.
 */
import { useCallback, useRef, useState } from "react";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, ShieldAlert, X } from "lucide-react";

export function useStepUp() {
  const [open, setOpen] = useState(false);
  const [purpose, setPurpose] = useState("generic");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [requires2FA, setRequires2FA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const resolverRef = useRef(null);

  const ensureStepUp = useCallback(({ purpose: p = "generic", resourceId } = {}) => {
    setPurpose(p);
    setPassword("");
    setTotp("");
    setError(null);
    // Détecte 2FA actif (silencieux)
    apiClient.get("/auth/2fa/status").then((r) => setRequires2FA(!!r.data?.enabled)).catch(() => {});
    setOpen(true);
    return new Promise((resolve) => {
      resolverRef.current = (val) => {
        resolverRef.current = null;
        setOpen(false);
        resolve(val);
      };
    });
  }, []);

  const submit = useCallback(async () => {
    if (!password && !totp) {
      setError("Mot de passe ou code 2FA requis.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.post("/auth/step-up", {
        password: password || undefined,
        totp_code: totp || undefined,
        purpose,
      });
      const t = data?.step_up_token;
      if (!t) throw new Error("Réponse invalide.");
      if (resolverRef.current) resolverRef.current(t);
    } catch (e) {
      setError(e?.response?.data?.detail || "Réauthentification refusée.");
    } finally {
      setLoading(false);
    }
  }, [password, totp, purpose]);

  const cancel = useCallback(() => {
    if (resolverRef.current) resolverRef.current(null);
  }, []);

  const StepUpDialog = open ? (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="step-up-dialog"
    >
      <Card className="w-full max-w-md p-6 bg-ivory border-copper/30 rounded-sm">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-copper" />
            <h3 className="font-serif italic text-xl text-night">Authentification renforcée</h3>
          </div>
          <button
            type="button"
            onClick={cancel}
            className="text-night/55 hover:text-night"
            data-testid="step-up-cancel"
            aria-label="Annuler"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[12px] text-night/70 leading-relaxed mb-4">
          Cette action est tracée et exige une réauthentification. Saisissez votre
          mot de passe{requires2FA ? " et/ou votre code 2FA" : ""}.
        </p>
        <div className="space-y-3">
          <input
            type="password"
            placeholder="Mot de passe"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 rounded-sm border border-sage bg-ivory text-sm focus:outline-none focus:border-copper"
            data-testid="step-up-password"
            autoFocus
          />
          {requires2FA && (
            <input
              type="text"
              inputMode="numeric"
              placeholder="Code 2FA (6 chiffres) — ou un code de secours"
              value={totp}
              onChange={(e) => setTotp(e.target.value)}
              className="w-full px-3 py-2 rounded-sm border border-sage bg-ivory text-sm focus:outline-none focus:border-copper font-mono"
              data-testid="step-up-totp"
            />
          )}
          {error && <p className="text-[12px] text-bordeaux" data-testid="step-up-error">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={cancel} className="rounded-sm" data-testid="step-up-cancel-btn">Annuler</Button>
            <Button
              onClick={submit}
              disabled={loading || (!password && !totp)}
              className="rounded-sm bg-petrol text-ivory hover:bg-night"
              data-testid="step-up-confirm-btn"
            >
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Vérification…</> : "Confirmer"}
            </Button>
          </div>
          <p className="text-[10px] text-night/45 italic leading-relaxed pt-1">
            L'action confirmée sera enregistrée dans le journal d'audit
            (date, IP, navigateur, niveau d'authentification).
          </p>
        </div>
      </Card>
    </div>
  ) : null;

  return { ensureStepUp, StepUpDialog };
}
